/**
 * 批量工时输入组件
 * 支持动态添加多行工时记录
 */
import React, { useState, useEffect } from 'react';
import { Row, Col, Select, InputNumber, Button, Space, Typography } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { Option } = Select;

export interface WorkTimeEntry {
  id: string;
  workerType: 'male' | 'female';
  workUnits: number;
  workerCount: number;
}

interface BatchWorkTimeInputProps {
  value?: WorkTimeEntry[];
  onChange?: (entries: WorkTimeEntry[]) => void;
  disabled?: boolean;
  maxRows?: number;
}

const BatchWorkTimeInput: React.FC<BatchWorkTimeInputProps> = ({
  value = [],
  onChange,
  disabled = false,
  maxRows = 10
}) => {
  const [entries, setEntries] = useState<WorkTimeEntry[]>(
    value.length > 0 ? value : [
      {
        id: Date.now().toString(),
        workerType: 'male',
        workUnits: 1,
        workerCount: 1
      }
    ]
  );

  // 同步外部值变化
  useEffect(() => {
    if (value && value.length > 0) {
      setEntries(value);
    }
  }, [value]);

  // 通知父组件数据变化
  const notifyChange = (newEntries: WorkTimeEntry[]) => {
    setEntries(newEntries);
    if (onChange) {
      onChange(newEntries);
    }
  };

  // 添加新行
  const addEntry = () => {
    if (entries.length >= maxRows) return;
    
    const newEntry: WorkTimeEntry = {
      id: Date.now().toString(),
      workerType: 'male',
      workUnits: 1,
      workerCount: 1
    };
    
    notifyChange([...entries, newEntry]);
  };

  // 删除行
  const removeEntry = (id: string) => {
    if (entries.length <= 1) return; // 至少保留一行
    
    const newEntries = entries.filter(entry => entry.id !== id);
    notifyChange(newEntries);
  };

  // 更新单行数据
  const updateEntry = (id: string, field: keyof Omit<WorkTimeEntry, 'id'>, value: any) => {
    const newEntries = entries.map(entry => 
      entry.id === id ? { ...entry, [field]: value } : entry
    );
    notifyChange(newEntries);
  };

  // 计算总工人数
  const getTotalWorkers = () => {
    return entries.reduce((total, entry) => total + (entry.workerCount || 0), 0);
  };

  return (
    <div>
      {/* 数据行 */}
      {entries.map((entry, index) => (
        <div 
          key={entry.id}
          style={{ 
            marginBottom: 12,
            padding: '12px',
            backgroundColor: '#fafafa',
            borderRadius: '8px',
            border: '1px solid #e8e8e8'
          }}
        >
          {/* 工人类型 */}
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ display: 'block', marginBottom: 6, color: '#333' }}>
              工人类型
            </Text>
            <Select
              value={entry.workerType}
              onChange={(value) => updateEntry(entry.id, 'workerType', value)}
              disabled={disabled}
              style={{ width: '100%' }}
              placeholder="请选择工人类型"
            >
              <Option value="male">👨 男工</Option>
              <Option value="female">👩 女工</Option>
            </Select>
          </div>

          {/* 工时和工人数量 - 横向排列 */}
          <Row gutter={12} style={{ marginBottom: 12 }}>
            <Col xs={12}>
              <Text strong style={{ display: 'block', marginBottom: 6, color: '#333' }}>
                工时
              </Text>
              <InputNumber
                value={entry.workUnits}
                onChange={(value) => updateEntry(entry.id, 'workUnits', value || 1)}
                min={0.01}
                max={999}
                precision={2}
                disabled={disabled}
                style={{ width: '100%' }}
                placeholder="工时"
              />
            </Col>
            
            <Col xs={12}>
              <Text strong style={{ display: 'block', marginBottom: 6, color: '#333' }}>
                工人数量
              </Text>
              <InputNumber
                value={entry.workerCount}
                onChange={(value) => updateEntry(entry.id, 'workerCount', value || 1)}
                min={1}
                max={999}
                precision={0}
                disabled={disabled}
                style={{ width: '100%' }}
                placeholder="人数"
              />
            </Col>
          </Row>

          {/* 操作按钮 */}
          <div style={{ textAlign: 'center', paddingTop: 8, borderTop: '1px dashed #e8e8e8' }}>
            <Space>
              {/* 添加按钮（只在最后一行显示） */}
              {index === entries.length - 1 && entries.length < maxRows && (
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={addEntry}
                  disabled={disabled}
                  style={{ color: '#52c41a', borderColor: '#52c41a' }}
                >
                  添加
                </Button>
              )}
              
              {/* 删除按钮（多于一行时显示） */}
              {entries.length > 1 && (
                <Button
                  type="text"
                  icon={<MinusCircleOutlined />}
                  onClick={() => removeEntry(entry.id)}
                  disabled={disabled}
                  danger
                >
                  删除
                </Button>
              )}
            </Space>
          </div>
        </div>
      ))}

      {/* 统计信息 */}
      <div style={{ 
        marginTop: 12, 
        padding: '8px 12px', 
        backgroundColor: '#f6ffed', 
        border: '1px solid #b7eb8f',
        borderRadius: '4px',
        textAlign: 'center'
      }}>
        <Text strong style={{ color: '#52c41a' }}>
          总计：{entries.length} 个工时条目，共 {getTotalWorkers()} 人
        </Text>
      </div>

      {/* 提示信息 */}
      {entries.length === 0 && (
        <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
          <Text type="secondary">请至少添加一个工时条目</Text>
        </div>
      )}
    </div>
  );
};

export default BatchWorkTimeInput;
